import React, { useContext } from 'react';
import { IterationContext, IterationContextType } from '../../contexts/IterationContext';
import { DeleteIcon } from 'assests/GmsIcons';
import { Add } from 'Components/DesignSystem/Iconography/Icons';
import Button from 'Components/DesignSystem/Button/Button';
import LeftButton, { RightButton } from 'Components/GMS/Rules/Components/LeftButton';
import Condition from './Condition';

type ConditionGroupProps = {
    conditionNode: ConditionNode;
    position: number;
    depth?: number;
    onRemove?: () => void;
};

const ConditionGroup: React.FC<ConditionGroupProps> = ({
    conditionNode,
    position,
    depth = 0,
    onRemove
}) => {
    const isMainCondition = conditionNode.type === 'operations' && depth === 0;
    const {
        activeIterationId,
        removeCondition,
        addCondition,
        indentCondition,
        updateLogicalOperator
    } = useContext<IterationContextType>(IterationContext);

    // If it's an operation condition, render the Condition component
    if (conditionNode.type === 'operations') {
        return (
            <Condition
                conditionId={conditionNode.id}
                position={position}
                isMainCondition={isMainCondition}
            />
        );
    }

    // If it's a nested condition, render the group
    const nestedCondition = conditionNode as NestedCondition;

    const handleDelete = () => {
        if (activeIterationId && onRemove) {
            onRemove();
        } else if (activeIterationId) {
            removeCondition(activeIterationId, conditionNode.id);
        }
    };

    const handleAddCondition = () => {
        if (activeIterationId) {
            addCondition(activeIterationId, conditionNode.id);
        }
    };

    const handleIndent = (direction: 'left' | 'right') => {
        if (activeIterationId) {
            indentCondition(activeIterationId, conditionNode.id, direction);
        }
    };

    const handleLogicalOperatorChange = (subConditionId: string) => (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (activeIterationId) {
            console.log(`Updating operator for sub-condition: ${subConditionId} to ${event.target.value}`);
            updateLogicalOperator(activeIterationId, subConditionId, event.target.value as LogicalOperator);
        }
    };

    const getSubConditionOperator = (subConditionId: string): LogicalOperator => {
        const subCondition = nestedCondition.conditions.find(c => c.id === subConditionId);
        return subCondition?.logical_operator || 'AND';
    };

    const hasSubConditions = () => nestedCondition?.conditions?.length > 0;

    return (
        <div className={`tw-bg-surface-sunken tw-py-6 tw-relative ${depth === 0 ? "tw-border tw-border-edge " : ""}`}>
            <div className="condition-wrapper">
                <div className="left-nesting-bracket" />
                <div className="tw-flex-1">
                    {!hasSubConditions && <div className="right-indent">
                        <RightButton onClick={() => handleIndent('right')} />
                        <div className="tw-py-1 tw-bg-surface"></div>
                        <LeftButton onClick={() => handleIndent('left')} />
                    </div>}

                    <div className="tw-col-span-full">
                        {nestedCondition.conditions.map((subCondition, index) => (
                            <div key={subCondition.id}>
                                {index > 0 && (
                                    <div className="tw-flex tw-justify-start tw-my-3">
                                        <select
                                            /* Token classes, not inline colour (§ 4.3): the inline
                                               `color: "black"` stayed black in dark. */
                                            className="form-select drop-down-dm tw-bg-surface tw-text-ink tw-border tw-border-solid tw-border-edge"
                                            value={getSubConditionOperator(nestedCondition.conditions[index - 1].id)}
                                            onChange={handleLogicalOperatorChange(nestedCondition.conditions[index - 1].id)}
                                            data-testid={`nested-logical-operator-${nestedCondition.conditions[index - 1].id}`}
                                            style={{
                                                textAlign: "center",
                                                fontSize: ".8rem",
                                                fontWeight: "600",
                                                width: "80px"
                                            }}
                                        >
                                            <option value="AND">And</option>
                                            <option value="OR">Or</option>
                                        </select>
                                    </div>
                                )}
                                <ConditionGroup
                                    conditionNode={subCondition}
                                    position={index}
                                    depth={depth + 1}
                                    onRemove={() => {
                                        if (activeIterationId) {
                                            removeCondition(activeIterationId, subCondition.id);
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="right-nesting-bracket" />

                {!hasSubConditions && <div className="mt-1 delete-btn-cba d-flex align-items-center">
                    <div>
                        <button aria-label="Delete"
                            className="delete-button icon-follow-ink tw-text-ink-tertiary"
                            onClick={handleDelete}
                            data-testid={`delete-condition-${conditionNode.id}`}
                        >
                            <DeleteIcon />
                        </button>
                    </div>
                </div>}
            </div>

            {/* tw-ml-8 aligns the button with the group's bracket (its 2rem
                margin-left) instead of the card edge. */}
            {depth === 0 && <div className="tw-mt-2 tw-ml-8">
                {/* § 4.5 grammar: the in-card add is tertiary sm with the DS Add glyph
                    (the baked-#0747A6 AddIcon and the never-compiled blue-50 hover are gone). */}
                <Button
                    variant="tertiary"
                    size="sm"
                    icon={<Add size={16} />}
                    iconPosition="start"
                    showIcon
                    onClick={handleAddCondition}
                    data-testid={`add-condition-${conditionNode.id}`}
                >
                    Add Condition
                </Button>
            </div>}
        </div>
    );
};

export default ConditionGroup;